import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { listingRunSchema } from "./runs"

// ── Per-run quality scorecard ───────────────────────────────────────────────
//
// The run scorecard is the outer quality ledger for issue #109: it combines the
// persisted run-item OK/NOK verdicts with append-only feed-build and marketplace
// import verdict audit rows. It is intentionally aggregate-first so operators
// can compare runs without shipping every SKU or import-error line.

export const runQualityReasonBucketSchema = z.object({
  reason: z.string(),
  count: z.number().int().nonnegative(),
})
export type RunQualityReasonBucket = z.infer<typeof runQualityReasonBucketSchema>

export const runQualityCoverageSchema = z.object({
  total: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  fixableNok: z.number().int().nonnegative(),
  businessBlockedNok: z.number().int().nonnegative(),
  sourceBlockedNok: z.number().int().nonnegative(),
  setAside: z.number().int().nonnegative(),
  okCoverageRatio: z.number().nullable(),
  fixableNokRatio: z.number().nullable(),
})
export type RunQualityCoverage = z.infer<typeof runQualityCoverageSchema>

export const runQualityNokBreakdownSchema = z.object({
  fixable: z.array(runQualityReasonBucketSchema),
  businessBlocked: z.array(runQualityReasonBucketSchema),
  sourceBlocked: z.array(runQualityReasonBucketSchema),
})
export type RunQualityNokBreakdown = z.infer<typeof runQualityNokBreakdownSchema>

export const runQualitySetAsideSchema = z.object({
  count: z.number().int().nonnegative(),
  reasons: z.array(runQualityReasonBucketSchema),
})
export type RunQualitySetAside = z.infer<typeof runQualitySetAsideSchema>

export const runQualityFeedBuildSchema = z.object({
  attempts: z.object({
    total: z.number().int().nonnegative(),
    ok: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
  }),
  latest: z
    .object({
      id: z.number().int(),
      ok: z.boolean(),
      createdAt: z.string(),
      rowCount: z.number().int().nullable(),
      configSkuCount: z.number().int().nullable(),
      message: z.string().nullable(),
      blockerCount: z.number().int().nonnegative(),
      blockers: z.array(runQualityReasonBucketSchema),
    })
    .nullable(),
})
export type RunQualityFeedBuild = z.infer<typeof runQualityFeedBuildSchema>

// `integration`/`transformation` are the two product-import (P44/P47) reports;
// `offer` is the OF01 offer-import (OF03) error report — persisted in the same
// audit table so offer rejections feed the same quality signals.
export const IMPORT_ERROR_REPORT_KINDS = ["integration", "transformation", "offer"] as const
export type ImportErrorReportKind = (typeof IMPORT_ERROR_REPORT_KINDS)[number]
/** Member references for call sites (`IMPORT_ERROR_REPORT_KIND.offer`), so the
 *  kind is never re-typed as a bare literal. */
export const IMPORT_ERROR_REPORT_KIND = {
  integration: "integration",
  transformation: "transformation",
  offer: "offer",
} as const satisfies { [K in ImportErrorReportKind]: K }
export const importErrorReportKindSchema = z.enum(IMPORT_ERROR_REPORT_KINDS)

/** Why a report line does or does not carry a product identifier. `sku: null` on
 *  its own used to mean all of these at once, so an unresolvable identifier read
 *  as the benign case (issue #269).
 *
 *  - `resolved`           — `sku` holds the identifier the report carried.
 *  - `not_expected`       — the marketplace declares no identifier column.
 *  - `column_missing`     — it declares one and this report does not carry it.
 *  - `value_missing`      — the column is there and this row's cell is empty.
 *  - `config_unavailable` — the marketplace's config could not be read, so
 *    whether an identifier was expected is unknown.
 *
 *  The last three are defects to report, not rows to guess at. */
export const IMPORT_ERROR_REPORT_IDENTIFIER_STATES = [
  "resolved",
  "not_expected",
  "column_missing",
  "value_missing",
  "config_unavailable",
] as const
export type ImportErrorReportIdentifierState =
  (typeof IMPORT_ERROR_REPORT_IDENTIFIER_STATES)[number]
export const importErrorReportIdentifierStateSchema = z.enum(IMPORT_ERROR_REPORT_IDENTIFIER_STATES)

export const importErrorReportLineSchema = z.object({
  lineNumber: z.number().int().positive(),
  sku: z.string().nullable(),
  /** How to read `sku` — a discriminated fact, not a substring to grep for. */
  identifierState: importErrorReportIdentifierStateSchema,
  /** Config SKU for the row, when the identifier is a seller SKU that the local
   *  catalog mirror maps. Always null when the marketplace's identifier is an
   *  EAN — and deliberately so even now that an EAN→product lookup exists
   *  (`importWorklistSchema`): one EAN can carry SEVERAL store products, and
   *  this row has one slot. Filling it would pick a product by coin flip;
   *  leaving it null and reporting the candidates per identifier in the worklist
   *  does not. Presenting the EAN itself here told the recovery agent to apply
   *  SKU-attribute overrides against a number that is no SKU (issue #269). */
  configSku: z.string().nullable(),
  /** The row's seller (Magento simple) SKU — null unless the marketplace's
   *  declared identifier IS a seller SKU. */
  simpleSku: z.string().nullable(),
  catalogMatched: z.boolean(),
  message: z.string(),
})
export type ImportErrorReportLine = z.infer<typeof importErrorReportLineSchema>

export const importErrorReportSchema = z.object({
  marketplace: marketplaceIdSchema,
  importId: z.string(),
  kind: importErrorReportKindSchema,
  filename: z.string(),
  contentType: z.string().nullable(),
  rowCount: z.number().int().nonnegative(),
  unmatchedSkuCount: z.number().int().nonnegative(),
  rows: z.array(importErrorReportLineSchema),
})
export type ImportErrorReport = z.infer<typeof importErrorReportSchema>

// ── Marketplace metadata refresh (issue #278) ────────────────────────────────
//
// Marketplace metadata used to be fetched once, ever: the first read of a
// category paged its attributes in and flagged it `attributes_synced`, and
// nothing ever re-read it. Value lists were worse — they were skipped whenever a
// row already existed, so the operator's allowed values could drift away from
// the cached copy with no path in the codebase to notice or repair it.
//
// `nova marketplaces refresh <marketplace> [category]` is that path, and these
// are its wire shapes. Both report REFETCHES, never "checked and kept": the
// refresh has no age threshold and no skip-if-present branch, so a code in
// `refreshed` was fetched from the marketplace and written over on this run.

/** What one unconditional value-list refetch pass did. Shared by the explicit
 *  refresh and the `2006`-triggered single-list refetch, so the two cannot
 *  describe the same work differently. */
export const valueListRefreshSchema = z.object({
  /** How many refetches were asked for — value lists referenced by the refreshed
   *  attributes, or lists named by an import's `2006` rejections. `0` with an
   *  empty `refreshed` means nothing asked, not "nothing changed". */
  triggered: z.number().int().nonnegative(),
  /** List codes fetched from the marketplace and written over on this run. */
  refreshed: z.array(z.string()),
  /** Value entries written across `refreshed` (each list is replaced whole, so
   *  this is the new entry count, not a delta). */
  entriesWritten: z.number().int().nonnegative(),
  /** Marketplace calls spent. One call can carry many lists — Mirakl VL11
   *  ignores its `values_list_codes` filter and answers with every list — so
   *  this is normally far below `refreshed.length`. */
  calls: z.number().int().nonnegative(),
  /** Codes asked for that the marketplace's response did not contain. Reported,
   *  never retried in a loop and never silently dropped: the cached copy is
   *  still whatever it was before. */
  missing: z.array(z.string()),
  /** Codes whose fetch or write failed, with the reason. */
  failed: z.array(z.object({ code: z.string(), error: z.string() })),
  /** Codes NOT fetched because the marketplace config supplies their entries
   *  itself (`valueListSubsets.staticEntries`) — there is no vendor list behind
   *  them to refresh. */
  configOwned: z.array(z.string()),
  /** Rejection messages a refetch could not be resolved from (item 2 only): the
   *  message carried a `2006` but nothing in it named a cached attribute or list.
   *  A hole in the trigger, reported so a changed vendor phrasing cannot make it
   *  quietly stop firing. */
  unresolved: z.array(z.string()),
  /** Codes a `2006` named whose cached copy was ALREADY rewritten after this
   *  import's rejections were recorded (item 2 only; always empty for the
   *  refresh command, which never skips anything).
   *
   *  Not an age threshold. The instant compared against belongs to the IMPORT, so
   *  a rejection cannot say anything about a copy that postdates it, and the
   *  answer does not change with the clock: `nova imports worklist` is explicitly
   *  re-runnable, and without this bound every re-run re-downloaded the whole
   *  48.9 MB value-list set and rewrote the same lists. */
  alreadyFresh: z.array(z.string()),
  /** Why the pass could not run AT ALL (unreadable marketplace config, …), or
   *  null when it ran. It belongs to the pass, not to any list: a whole-refetch
   *  failure once rode in `failed[]` under a `(unresolved)` sentinel code, which
   *  the CLI then printed as though a value list were named that. */
  refetchError: z.string().nullable(),
})
export type ValueListRefresh = z.infer<typeof valueListRefreshSchema>

export const metadataRefreshReportSchema = z.object({
  marketplace: marketplaceIdSchema,
  /** The metadata key that was refreshed (the Mirakl backend host, shop-scoped
   *  for shops with divergent schemas). This is what the rows are keyed by, so
   *  it — not the marketplace — is the unit of work. */
  schemaSource: z.string(),
  /** Other configured marketplaces keyed to the same schema source. They read
   *  the rows this run rewrote, so refreshing one of them is refreshing all of
   *  them, and a second cron job per sibling would only re-walk the same rows. */
  sharedWith: z.array(marketplaceIdSchema),
  /** `category` — one named category and its whole ancestor chain, whatever
   *  their `attributes_synced` flags say. `cached` — every category already
   *  cached for this schema source (the full sweep). */
  scope: z.enum(["category", "cached", "taxonomy"]),
  /** EVERY REQUESTED CATEGORY LANDS IN EXACTLY ONE BUCKET:
   *  `requested === refreshed.length + partial.length + failed.length`. It used
   *  to not: `refreshed` was filtered down to the categories whose value lists
   *  ALSO landed, so a run where four categories fetched cleanly but the value-list
   *  response carried nothing reported `requested: 4, refreshed: ["111","112"],
   *  failed: []` — two categories in neither bucket, with their attributes
   *  written. `partial` is where those go. */
  hierarchies: z.object({
    /** Categories this run set out to refetch. */
    requested: z.number().int().nonnegative(),
    /** Categories fully refreshed: attributes refetched and written, AND every
     *  value list they reference refetched. Only these are flagged
     *  `attributes_synced`. */
    refreshed: z.array(z.string()),
    /** Categories whose ATTRIBUTES were refetched and written, but at least one
     *  value list they reference was not (failed, or absent from the response).
     *  Their attribute rows are current; their allowed values may not be. They
     *  are not flagged `attributes_synced`, so a category cached for the first
     *  time by this run still reads as uncached until a later run completes it. */
    partial: z.array(z.string()),
    /** Categories whose attribute fetch or write failed, with the reason. Their
     *  cached attributes are unchanged — i.e. still as stale as they were. */
    failed: z.array(z.object({ code: z.string(), error: z.string() })),
  }),
  /** Attribute rows written across the refreshed categories. */
  attributesWritten: z.number().int().nonnegative(),
  /** Response entries the parser could not read (no readable attribute code),
   *  across the whole walk. Non-zero means at least one category was REFUSED for
   *  it: a refresh deletes a category's rows and inserts what it read, so a
   *  response nova only half-understands must not authorise that. These used to
   *  be dropped on a bare `continue` — no log, no count — which is how a partial
   *  parse deleted the entries it could not read and still reported success. */
  attributesSkipped: z.number().int().nonnegative(),
  /** `hierarchy_code` values the marketplace's responses carried that are not in
   *  nova's cached taxonomy, deduped. Rows tagged with one are DROPPED, never
   *  written: they would land under a key no read can reach. A non-empty list
   *  usually means either the vendor added categories (run
   *  `make sync-mirakl-cache TAXONOMY_ONLY=1`) or it changed the formatting of a
   *  code nova already knows. */
  unknownHierarchyCodes: z.array(z.string()),
  valueLists: valueListRefreshSchema,
  /** True only when every requested category and every referenced value list was
   *  refetched. False means part of the cache is still stale — which is the whole
   *  defect this refresh exists to fix, so `nova marketplaces refresh` exits
   *  non-zero on it rather than reporting a green tick that changed nothing. */
  complete: z.boolean(),
  durationMs: z.number().int().nonnegative(),
})
export type MetadataRefreshReport = z.infer<typeof metadataRefreshReportSchema>

/** `POST /api/marketplaces/:marketplace/metadata/refresh` body. `hierarchy`
 *  narrows the refresh to one category and its ancestors; omitted refreshes
 *  every category already cached. There is deliberately no "only if older than"
 *  option — an age threshold just replaces one invented number with another, and
 *  the flag that said "we have data" is what went stale in the first place. */
export const metadataRefreshBodySchema = z.object({
  hierarchy: z.string().trim().min(1).optional(),
})
export type MetadataRefreshBody = z.infer<typeof metadataRefreshBodySchema>

// ── Import error worklist (issue #269 item 4) ────────────────────────────────
//
// `importErrorReportSchema` above is the per-LINE view of ONE report kind: what
// the CSV said, row by row. The worklist is the per-IDENTIFIER view across BOTH
// product-import report kinds: what the operator has to go and fix, one entry
// per product rather than one per row. A product rejected on four feed lines,
// twice in transformation and twice in integration, is ONE thing to fix.
//
// It reports. It never re-uploads: the autoloop this issue originally planned was
// demoted because nova's fixers cannot repair the dominant rejection class, so
// fixing stays in the foreground where a person is present (issue #269, "What
// changed on 2026-08-03"). There is no flag that would make it write.

/** How the marketplace's declared error-report identifier resolves to a catalog
 *  product, per `config.importErrorReportIdentifier.kind`.
 *
 *  - `sku`     — a seller SKU; the catalog mirror is keyed on it directly.
 *  - `ean`     — a GTIN; resolved through the store-scoped EAN lookup, which may
 *    answer with zero, one, or several products.
 *  - `none`    — the marketplace declares no identifier column. Nothing to
 *    resolve, and nothing missing.
 *  - `unknown` — its config could not be read, so whether an identifier was
 *    expected is unknown. A defect, not a benign absence. */
export const IMPORT_WORKLIST_IDENTIFIER_AXES = ["sku", "ean", "none", "unknown"] as const
export type ImportWorklistIdentifierAxis = (typeof IMPORT_WORKLIST_IDENTIFIER_AXES)[number]
export const importWorklistIdentifierAxisSchema = z.enum(IMPORT_WORKLIST_IDENTIFIER_AXES)

/** What the catalog mirror answered for one worklist entry's identifier.
 *
 *  - `resolved`      — exactly one store product carries it. `configSku` /
 *    `simpleSku` are populated and safe to act on.
 *  - `unresolved`    — the identifier is readable and NO store product carries
 *    it. A reported outcome, never a dropped row: the operator sees an identifier
 *    the catalog does not know.
 *  - `ambiguous`     — SEVERAL store products carry it (only reachable on the
 *    `ean` axis; the mirror's primary key is `(simple_sku, store)`). The entry has
 *    NO `configSku` / `simpleSku` field at all — the item union is discriminated
 *    on this very field, so there is nothing to misread — and every match is
 *    listed in `candidates`; picking one would be a coin flip presented as an
 *    answer.
 *  - `not_attempted` — there was nothing to resolve: the row carries no readable
 *    identifier (see `identifierState`), or the marketplace's axis is
 *    `none` / `unknown`. */
export const IMPORT_WORKLIST_RESOLUTIONS = [
  "resolved",
  "unresolved",
  "ambiguous",
  "not_attempted",
] as const
export type ImportWorklistResolution = (typeof IMPORT_WORKLIST_RESOLUTIONS)[number]
export const importWorklistResolutionSchema = z.enum(IMPORT_WORKLIST_RESOLUTIONS)

/** The report kinds a PRODUCT import has, in Mirakl's own stage order:
 *  transformation (stage 1) transforms the uploaded file, integration (stage 2)
 *  sends the surviving rows on. Deliberately narrower than
 *  `IMPORT_ERROR_REPORT_KINDS`, which also carries `offer` — the OF03 offer
 *  report belongs to a different import entirely and a product worklist can
 *  never hold one, so the wire type must not admit it. */
export const IMPORT_WORKLIST_KINDS = ["transformation", "integration"] as const
export type ImportWorklistKind = (typeof IMPORT_WORKLIST_KINDS)[number]
export const importWorklistKindSchema = z.enum(IMPORT_WORKLIST_KINDS)

/** One store product an identifier matched. */
export const importWorklistCandidateSchema = z.object({
  configSku: z.string(),
  simpleSku: z.string(),
})
export type ImportWorklistCandidate = z.infer<typeof importWorklistCandidateSchema>

/** One errored report row behind a worklist entry. */
export const importWorklistRowSchema = z.object({
  kind: importWorklistKindSchema,
  lineNumber: z.number().int().positive(),
  message: z.string(),
})
export type ImportWorklistRow = z.infer<typeof importWorklistRowSchema>

/** What every worklist entry carries whatever its resolution: which identifier
 *  (or which reason it has none) and every errored row behind it. */
const importWorklistItemBase = {
  /** The identifier the report carried, or null when the rows behind this entry
   *  carry none — then `identifierState` names why and the entry groups every
   *  such row for that reason. */
  identifier: z.string().nullable(),
  identifierState: importErrorReportIdentifierStateSchema,
  rowCount: z.number().int().nonnegative(),
  rows: z.array(importWorklistRowSchema),
}

/** One thing to fix, DISCRIMINATED on `resolution` so which fields exist is a
 *  type fact rather than a comment: `configSku` / `simpleSku` are reachable only
 *  on the `resolved` member and `candidates` only on `ambiguous`, so nothing can
 *  read a SKU off an entry that never resolved to one. Same shape the CLI's
 *  offer-upload verdict uses (issue #276). */
export const importWorklistItemSchema = z.discriminatedUnion("resolution", [
  /** Exactly one store product carries the identifier — safe to act on. */
  z.object({
    ...importWorklistItemBase,
    resolution: z.literal("resolved"),
    configSku: z.string(),
    simpleSku: z.string(),
  }),
  /** SEVERAL store products carry it (only reachable on the `ean` axis). Every
   *  match is named and none is chosen — picking one would be a coin flip
   *  presented as an answer, so there is no `configSku` field to read. */
  z.object({
    ...importWorklistItemBase,
    resolution: z.literal("ambiguous"),
    candidates: z.array(importWorklistCandidateSchema).min(2),
  }),
  /** The identifier is readable and NO store product carries it. */
  z.object({ ...importWorklistItemBase, resolution: z.literal("unresolved") }),
  /** Nothing to resolve: the rows carry no readable identifier (see
   *  `identifierState`), or the marketplace's axis is `none` / `unknown`. */
  z.object({ ...importWorklistItemBase, resolution: z.literal("not_attempted") }),
])
export type ImportWorklistItem = z.infer<typeof importWorklistItemSchema>

/** What one report kind contributed to the worklist. A kind the marketplace
 *  could not serve is a LINE here, not a failed request — an import that failed
 *  in transformation has no integration report at all, and losing the whole
 *  worklist to that would hide the rows that do exist.
 *
 *  Two very different absences live here and `applicable` tells them apart: a
 *  report that CANNOT exist (nothing was rejected at that stage, or the stage
 *  never ran) versus one that should exist and could not be read. Only the second
 *  is counted against `verified` below — a flag that goes false on a routine
 *  outcome is a flag operators learn to ignore, which costs the real signal. */
export const importWorklistSourceSchema = z.object({
  kind: importWorklistKindSchema,
  fetched: z.boolean(),
  /** Whether this report kind can exist for this import at all, per the import's
   *  OWN state. False = the import proves no such report exists, so the failed
   *  download is the expected answer: not a gap, and it never flips `verified`.
   *  True whenever the download succeeded, whenever the failure was anything but
   *  a 404, and whenever the import's state cannot settle the question — the
   *  unsettled case is deliberately loud, because a partial worklist an operator
   *  believes is complete is the failure mode of the whole surface. */
  applicable: z.boolean(),
  /** Why no such report can exist, when `applicable` is false; null otherwise. */
  inapplicableReason: z.string().nullable(),
  /** Rows THIS request's download PARSED out of the report CSV — null when the
   *  fetch failed, because nothing was parsed.
   *
   *  Reported next to `storedRowCount` rather than in place of it because the two
   *  can differ, and every way they differ is a defect that reading only the
   *  table hides: a report whose bytes arrive but whose rows never land (a
   *  delimiter/shape break, a `line number` past the `int4` column, an insert
   *  that threw) answered byte-identically to a genuinely clean import
   *  (issue #269). */
  parsedRowCount: z.number().int().nonnegative().nullable(),
  /** Rows the audit table holds for this kind AFTER the fetch, so a kind whose
   *  fetch failed still reports whatever an earlier download persisted. */
  storedRowCount: z.number().int().nonnegative(),
  /** Parsed rows the table does not hold — `parsedRowCount − storedRowCount`,
   *  floored at 0. ALWAYS a defect when non-zero, and always a `rows_lost`
   *  entry in `unverified`: the commonest cause is two report rows sharing a
   *  `line number`, where the audit table's unique
   *  `(marketplace, import id, kind, line)` index (inherited from issue #105)
   *  keeps one and drops the other. The dropped row is a different product, so
   *  the worklist says so instead of silently omitting it. */
  lostRowCount: z.number().int().nonnegative(),
  /** The FETCH failure for this kind, or null. */
  error: z.string().nullable(),
  /** The PARSE/PERSIST failure for this kind, or null. The download's persist is
   *  best-effort by design (the recovery flow must still get the CSV), so its
   *  failure is surfaced here instead of only in a log line. */
  persistError: z.string().nullable(),
})
export type ImportWorklistSource = z.infer<typeof importWorklistSourceSchema>

/** One of the import's own three-valued rejected-row counters, mirroring the
 *  Mirakl adapter's `ImportCounterReading`. ABSENT IS NOT ZERO: `not_gradeable`
 *  means the vendor never reported the number, and no "nothing to fix" verdict
 *  may be derived from it (issue #269). */
export const importWorklistCounterSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("errors"), count: z.number().int().positive() }),
  z.object({ state: z.literal("clean") }),
  z.object({ state: z.literal("not_gradeable") }),
])
export type ImportWorklistCounter = z.infer<typeof importWorklistCounterSchema>

/** The import's own rejected-row counters, keyed by the report kind each one
 *  grades: `transform_lines_in_error` for stage 1,
 *  `integration_details.rejected_products` for stage 2. Keyed rather than named
 *  so the per-kind cross-check below is a lookup, not a branch. */
export const importWorklistCountersSchema = z.object({
  transformation: importWorklistCounterSchema,
  integration: importWorklistCounterSchema,
})
export type ImportWorklistCounters = z.infer<typeof importWorklistCountersSchema>

/** ONE reason the worklist cannot claim to be the whole truth, discriminated on
 *  `kind` so a new reason is a compile error at every reader rather than another
 *  free-text line. Every member carries a `detail` sentence for the CLI to print
 *  verbatim.
 *
 *  Any entry at all means `verified: false`, and `nova imports worklist` exits
 *  non-zero — the same vocabulary and the same exit code as `nova offers upload`
 *  (issue #276), because an operator must be able to tell "checked, nothing to
 *  fix" from "could not check". */
export const importWorklistGapSchema = z.discriminatedUnion("kind", [
  /** The download of a report the import says SHOULD be readable failed, so this
   *  kind contributed nothing this run. A report the import proves cannot exist
   *  is not this — it is an `applicable: false` source line and no gap. */
  z.object({
    kind: z.literal("report_unfetched"),
    reportKind: importWorklistKindSchema,
    detail: z.string(),
  }),
  /** The parse read rows the audit table does not hold (see `lostRowCount`).
   *
   *  CHOSEN, though its commonest cause is a vendor quirk nova cannot repair (two
   *  report rows sharing a `line number`): the dropped row is a DIFFERENT product
   *  missing from the worklist, so the operator must learn it before treating the
   *  list as complete. Costing the exit code is the point, not a side effect. */
  z.object({
    kind: z.literal("rows_lost"),
    reportKind: importWorklistKindSchema,
    parsedRowCount: z.number().int().nonnegative(),
    storedRowCount: z.number().int().nonnegative(),
    detail: z.string(),
  }),
  /** The import counted MORE rejections than this kind's report accounts for —
   *  none of them, or only some — an upstream contract break, not a clean
   *  nothing-to-fix (the same guard `planOfferRecoveryFromReport` applies to
   *  OF02/OF03). The comparison differs per kind because the counters do:
   *  transformation's counts LINES (comparable to the report's yield directly),
   *  integration's counts PRODUCTS (compared against the distinct products the
   *  rows named, so a product rejected on several lines is not a false alarm). */
  z.object({
    kind: z.literal("counter_contradicts_report"),
    reportKind: importWorklistKindSchema,
    /** What the import's own counter claimed — LINES for transformation,
     *  PRODUCTS for integration. */
    claimedRows: z.number().int().positive(),
    detail: z.string(),
  }),
  /** The kind contributed no rows and the counter that would prove that correct
   *  was never reported. Absence is not evidence of zero. Raised only for a stage
   *  that could have rejected rows: a stage the import proves never ran has no
   *  rows to be absent, which is a different statement and not this one.
   *
   *  It costs the exit code deliberately, and cheaply: Mirakl does report these
   *  counters, so an unreported one is rare — and when it happens, nothing at all
   *  backs an empty worklist. */
  z.object({
    kind: z.literal("counter_not_gradeable"),
    reportKind: importWorklistKindSchema,
    detail: z.string(),
  }),
  /** The import's status read failed, so NO counter could be cross-checked. */
  z.object({ kind: z.literal("import_status_unreadable"), detail: z.string() }),
])
export type ImportWorklistGap = z.infer<typeof importWorklistGapSchema>

export const importWorklistSchema = z.object({
  marketplace: marketplaceIdSchema,
  importId: z.string(),
  identifier: z.object({
    axis: importWorklistIdentifierAxisSchema,
    /** The declared report column, or null when the axis is `none` / `unknown`. */
    column: z.string().nullable(),
  }),
  /** True only when every report kind was read, every parsed row reached the
   *  table, and the import's own counters back what the worklist reports. An
   *  EMPTY worklist means "nothing to fix" ONLY when this is true — that is the
   *  whole point of the flag, because a broken report and a clean import used to
   *  render identically (issue #269). */
  verified: z.boolean(),
  /** Every reason `verified` is false; empty when it is true. */
  unverified: z.array(importWorklistGapSchema),
  /** The import's own rejected-row counters, as read for the cross-check. */
  counters: importWorklistCountersSchema,
  sources: z.array(importWorklistSourceSchema),
  /** The five resolution counts PARTITION `items` — every entry lands in exactly
   *  one, derived from one exhaustive switch, so
   *  `resolved + unresolved + ambiguous + unidentified + notAttempted === items`
   *  always holds and no entry can fall through into no bucket. */
  totals: z.object({
    /** Errored report rows across both kinds. */
    rows: z.number().int().nonnegative(),
    /** Worklist entries — the number of things to fix. */
    items: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    unresolved: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    /** Entries whose rows carried no readable identifier at all. */
    unidentified: z.number().int().nonnegative(),
    /** Entries with a READABLE identifier that was never looked up, because the
     *  marketplace's axis is `none` / `unknown` and nova cannot say what the
     *  value means. */
    notAttempted: z.number().int().nonnegative(),
  }),
  items: z.array(importWorklistItemSchema),
  /** What the import's `2006` value-not-in-list rejections caused nova to
   *  refetch (issue #278 item 2). Always present, never null: a missing object
   *  would read the same as "no rejection was acted on", which is the silence
   *  this field exists to remove. `triggered: 0` says no `2006` was seen. */
  valueListRefresh: valueListRefreshSchema,
})
export type ImportWorklist = z.infer<typeof importWorklistSchema>

export const runQualityImportRejectionsSchema = z.object({
  total: z.number().int().nonnegative(),
  byKind: z.array(
    z.object({
      kind: importErrorReportKindSchema,
      count: z.number().int().nonnegative(),
      skuCount: z.number().int().nonnegative(),
    })
  ),
  byMessage: z.array(
    z.object({
      kind: importErrorReportKindSchema,
      message: z.string(),
      count: z.number().int().nonnegative(),
      skuCount: z.number().int().nonnegative(),
    })
  ),
})
export type RunQualityImportRejections = z.infer<typeof runQualityImportRejectionsSchema>

export const runQualityScorecardSchema = z.object({
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  generatedAt: z.string(),
  run: listingRunSchema,
  coverage: runQualityCoverageSchema,
  nok: runQualityNokBreakdownSchema,
  setAside: runQualitySetAsideSchema,
  feedBuild: runQualityFeedBuildSchema,
  marketplaceImportRejections: runQualityImportRejectionsSchema,
})
export type RunQualityScorecard = z.infer<typeof runQualityScorecardSchema>
